import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../../api/client";
import DynamicPosts from "../DynamicPosts/DynamicPosts";
import "./Banner.css";

/* ✅ Client-priority order */
const PRIORITY_CATEGORIES = [
  "Textile Industries",
  "Incense and Dhoop Industries",
  "Others",
];

function Banner() {
  const navigate = useNavigate();

  const [category, setCategory] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [popularCategories, setPopularCategories] = useState([]);

  // 🔹 Fetch categories from backend
  useEffect(() => {
    async function fetchCategories() {
      try {
        const res = await client.get("/api/categories");
        if (res.data.ok && Array.isArray(res.data.categories)) {
          const all = res.data.categories;
          setCategories(all);

          /* ✅ Force priority categories first */
          const priority = PRIORITY_CATEGORIES
            .map((name) => all.find((c) => c.name === name))
            .filter(Boolean);

          const remaining = all.filter(
            (c) => !PRIORITY_CATEGORIES.includes(c.name)
          );

          setPopularCategories([...priority, ...remaining].slice(0, 6));
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    }

    fetchCategories();
  }, []);

  // 🔹 Handle category click
  const handleCategoryClick = (cat) => {
    if (!cat) return;

    setCategory(cat.name);
    navigate(
      `/category/${cat.slug || cat.name.toLowerCase().replace(/\s+/g, "-")}`
    );
    setIsDropdownOpen(false);
  };

  return (
    <div className="bannerParentDiv">
      <div className="bannerChildDiv">
        {/* 🔴 NOTHING BELOW IS CHANGED 🔴 */}

        {/* Hero Section */}
        <div className="heroSection">
          <div className="heroContent">
            <div className="heroBadge">
              <span>Industrial Machinery Multipliers</span>
            </div>

            <h1 className="heroTitle">
              FIND YOUR PERFECT
              <span className="titleHighlight"> INDUSTRIAL MACHINERY</span>
            </h1>

            <p className="heroSubtitle">
              Buy and sell quality used industrial machinery from verified global suppliers.
              From old field equipment to modern factory solutions, find the right machines for your needs.
            </p>

            <div className="ctaButtons">
              <button className="ctaPrimary" onClick={() => navigate("/allposts")}>
                Browse Machines
              </button>
              <button className="ctaSecondary" onClick={() => navigate("/create")}>
                Sell Your Machine
              </button>
            </div>

            <div className="quickStats">
              <div className="statItem">
                <span className="statNumber">10K+</span>
                <span className="statLabel">Machines Listed</span>
              </div>
              <div className="statItem">
                <span className="statNumber">500+</span>
                <span className="statLabel">Trusted Suppliers</span>
              </div>
              <div className="statItem">
                <span className="statNumber">50+</span>
                <span className="statLabel">Countries Served</span>
              </div>
            </div>
          </div>

          <div className="bannerVisual">
            <div className="bannerImageContainer">
              <img
                src="/desktop.jpg"
                alt="Industrial Machinery Marketplace"
                className="bannerImage desktopImage"
              />
              <img
                src="/mobile.png"
                alt="Industrial Machinery Marketplace"
                className="bannerImage mobileImage"
              />

            </div>
          </div>
        </div>

        {/* Categories Section */}
        <div className="categoriesSection">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Browse by Industry</h2>
            <p className="sectionSubtitle">
              Find machinery for your specific industrial needs
            </p>
          </div>

          {/* Mobile Dropdown */}
          <div className="categoryDropdownMobile">
            <select
              value={category || ""}
              onChange={(e) => {
                const selected = categories.find(
                  (c) => c.name === e.target.value
                );
                if (selected) handleCategoryClick(selected);
              }}
              className="categorySelect"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Popular Categories */}
          <div className="popularCategories">
            {popularCategories.map((cat) => (
              <div
                key={cat.id}
                className={`categoryCard ${category === cat.name ? "active" : ""
                  }`}
                onClick={() => handleCategoryClick(cat)}
              >
                <div className="categoryIcon">
                  {getCategoryIcon(cat.name)}
                </div>
                <span className="categoryName">{cat.name}</span>
              </div>
            ))}
          </div>

          {/* View All */}
          <div className="viewAllCategories">
            <button
              className="viewAllButton"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              View All Categories
            </button>

            {isDropdownOpen && (
              <div className="allCategoriesDropdown">
                <div className="dropdownGrid">
                  {categories.map((cat) => (
                    <div
                      key={cat.id}
                      className="dropdownCategory"
                      onClick={() => handleCategoryClick(cat)}
                    >
                      {getCategoryIcon(cat.name)} {cat.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {category && <DynamicPosts category={category} />}
    </div>
  );
}

/* 🔹 Fixed icon keys */
function getCategoryIcon(category) {
  const icons = {
    "Textile Industries": "🧵",
    "Incense and Dhoop Industries": "🪔",
    "Food Industries": "🍕",
    "Pharma Industries": "💊",
    "Construction": "🏗️",
    "Cosmetic": "💄",
    "Primary Packaging": "📦",
    "Secondary Packaging": "🎁",
    "Stationary": "📐",
    "Parts Counting Machinery": "🔢",
    "Parts Assembly Machine": "⚙️",
    "Special Purpose Machines": "🛠️",
    "Gems and Jewellery": "💎",
    "Non Woven Product Machinery": "🧻",
    "Others": "📦",
  };

  return icons[category] || "🏭";
}

export default Banner;
